import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  IconButton,
  Grid,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TableContainer,
  CircularProgress,
} from "@mui/material";
import { ArrowBack } from "@mui/icons-material";
import { getInstitutePromotionLogDetails } from "../../services/studentPromotionService";

function fmt(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString();
  } catch {
    return String(iso);
  }
}

export default function PromotionHistoryDetail() {
  const navigate = useNavigate();
  const { historyId } = useParams();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setLoading(true);
      try {
        const res = await getInstitutePromotionLogDetails(historyId, { windowDays: 30 });
        if (mounted) setData(res?.data?.data || null);
      } catch {
        if (mounted) setData(null);
      } finally {
        if (mounted) setLoading(false);
      }
    };
    load();
    return () => {
      mounted = false;
    };
  }, [historyId]);

  const h = data?.history || null;
  const a = data?.activity || {};

  return (
    <Box sx={{ p: 2 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 2 }}>
        <IconButton onClick={() => navigate(-1)} aria-label="Back">
          <ArrowBack />
        </IconButton>
        <Typography variant="h6" fontWeight={700}>
          Promotion details
        </Typography>
      </Box>

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
          <CircularProgress />
        </Box>
      ) : !data ? (
        <Typography color="text.secondary">No data found.</Typography>
      ) : (
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Summary
                </Typography>
                <Typography variant="body2">Student: {data.student?.name || "—"} ({data.student?.studentLoginID || "—"})</Typography>
                <Typography variant="body2">From: {h?.fromClassLabel || "—"} → To: {h?.toClassLabel || "—"}</Typography>
                <Typography variant="body2">When: {fmt(h?.promotedAt)}</Typography>
                <Typography variant="body2">Effective: {fmt(h?.effectiveDate)}</Typography>
                <Typography variant="body2">By: {h?.promotedByName || "—"}</Typography>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12}>
            <Card sx={{ width: "100%" }}>
              <CardContent sx={{ px: { xs: 2, sm: 3 }, py: 2 }}>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Attendance (window)
                </Typography>
                <Typography variant="body2" sx={{ mb: 2 }}>
                  Sessions: {a.attendance?.sessionCount || 0} • Marked rows: {a.attendance?.markedCount || 0}
                </Typography>
                <TableContainer sx={{ width: "100%", overflowX: "auto" }}>
                  <Table size="small" sx={{ minWidth: 720 }}>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>Subject</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>Lecture date</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>Method</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>Status</TableCell>
                        <TableCell sx={{ whiteSpace: "nowrap" }}>Updated</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {(a.attendance?.rows || []).map((r) => (
                        <TableRow key={r.id}>
                          <TableCell>{r.subjectName || "—"}</TableCell>
                          <TableCell>
                            {r.lectureDate || (r.sessionStartTime ? fmt(r.sessionStartTime) : "—")}
                          </TableCell>
                          <TableCell>{r.method || "—"}</TableCell>
                          <TableCell>{r.status || "—"}</TableCell>
                          <TableCell sx={{ whiteSpace: "nowrap" }}>{fmt(r.updatedAt)}</TableCell>
                        </TableRow>
                      ))}
                      {(a.attendance?.rows || []).length === 0 && (
                        <TableRow>
                          <TableCell colSpan={5} align="center">
                            <Typography variant="body2" color="text.secondary">
                              No attendance rows in this window.
                            </Typography>
                          </TableCell>
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Assignments (submissions in window)
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Total submissions: {a.assignments?.submissionCount || 0}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Assignment</TableCell>
                      <TableCell>Subject</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell>Score</TableCell>
                      <TableCell>Submitted</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(a.assignments?.rows || []).slice(0, 25).map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.assignmentName || r.assignmentId}</TableCell>
                        <TableCell>{r.subject || "—"}</TableCell>
                        <TableCell>{r.status || "—"}</TableCell>
                        <TableCell>
                          {r.score != null ? `${r.score}/${r.maxScore || r.assignmentMaxMarks || "—"}` : "—"}
                        </TableCell>
                        <TableCell>{fmt(r.submittedAt)}</TableCell>
                      </TableRow>
                    ))}
                    {(a.assignments?.rows || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No submissions in this window.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Exams (scores in window)
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Total: {a.exams?.scoreCount || 0}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Exam</TableCell>
                      <TableCell>Type</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Marks</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(a.exams?.rows || []).slice(0, 25).map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.examName || r.examId}</TableCell>
                        <TableCell>{r.examType || "—"}</TableCell>
                        <TableCell>{fmt(r.examDate)}</TableCell>
                        <TableCell>
                          {r.marksObtained != null ? `${r.marksObtained}/${r.maxMarks ?? "—"}` : "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {(a.exams?.rows || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No exam scores in this window.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Typography fontWeight={700} sx={{ mb: 1 }}>
                  Materials (created in window)
                </Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Total: {a.materials?.count || 0}
                </Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Created</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {(a.materials?.rows || []).slice(0, 25).map((r) => (
                      <TableRow key={r.id}>
                        <TableCell>{r.name || r.id}</TableCell>
                        <TableCell>{fmt(r.createdAt)}</TableCell>
                      </TableRow>
                    ))}
                    {(a.materials?.rows || []).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={2} align="center">
                          <Typography variant="body2" color="text.secondary">
                            No materials in this window.
                          </Typography>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}
    </Box>
  );
}

